import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { budgetAlertsAPI, budgetsAPI } from '../../data/budgets';
import { unwrap } from '../../data/api';
import { categoryBudgetsAPI } from '../../data/categoryBudgets';
import { OVERVIEW_STALE_TIME } from '../../lib/queryDefaults';
import { expensesAPI } from '../../data/expenses';
import { invalidateGuidedFlow } from '../guidedFlow/queries';

export const budgetsKeys = {
  all: ['budgets'],
  active: () => [...budgetsKeys.all, 'active'],
  presets: () => [...budgetsKeys.all, 'presets'],
  overview: ({ year, month, periodStart, periodEnd, includeArchivedWithSpending }) => [
    ...budgetsKeys.all,
    'overview',
    year,
    month,
    periodStart,
    periodEnd,
    includeArchivedWithSpending ?? false,
  ],
  alerts: (filters) => [...budgetsKeys.all, 'alerts', filters || {}],
  categoryStatus: (categoryId, { year, month }) => [
    ...budgetsKeys.all,
    'category-status',
    categoryId,
    year,
    month,
  ],
  categoryHistory: (categoryId) => [
    ...budgetsKeys.all,
    'category-history',
    categoryId,
  ],
};


export function useActiveMethodQuery() {
  return useQuery({
    queryKey: budgetsKeys.active(),
    queryFn: () => budgetsAPI.getActiveMethod().then((r) => unwrap(r)),
  });
}

export function usePresetsQuery(queryOptions = {}) {
  return useQuery({
    queryKey: budgetsKeys.presets(),
    queryFn: () => budgetsAPI.getPresets().then((r) => unwrap(r, [])),
    ...queryOptions,
  });
}

export function useCreateBudgetMethodMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      budgetsAPI.createMethodFromPreset(payload).then((r) => unwrap(r)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: budgetsKeys.all, exact: false });
      qc.invalidateQueries({ queryKey: ['expenses', 'categories'], exact: false });
    },
  });
}

export function useSetPlanningIncomeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      budgetsAPI.setPlanningIncome(payload).then((r) => unwrap(r)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: budgetsKeys.all, exact: false });
    },
  });
}

export function useBudgetOverviewQuery({
  year,
  month,
  periodStart,
  periodEnd,
  includeArchivedWithSpending = false,
  enabled = true,
}) {
  return useQuery({
    queryKey: budgetsKeys.overview({
      year,
      month,
      periodStart,
      periodEnd,
      includeArchivedWithSpending,
    }),
    queryFn: () =>
      budgetsAPI
        .getOverview({
          year,
          month,
          periodStart,
          periodEnd,
          includeArchivedWithSpending,
        })
        .then((r) => unwrap(r)),
    enabled: enabled && Boolean(periodStart && periodEnd),
    staleTime: OVERVIEW_STALE_TIME,
    placeholderData: (prev) => prev,
  });
}

export function useCategoryBudgetStatusQuery(categoryId, { year, month, enabled = true } = {}) {
  return useQuery({
    queryKey: budgetsKeys.categoryStatus(categoryId, { year, month }),
    queryFn: () =>
      categoryBudgetsAPI
        .getCategoryStatus(categoryId, { year, month })
        .then((r) => unwrap(r)),
    enabled: enabled && Boolean(categoryId && year && month),
  });
}

export function useCategoryBudgetHistoryQuery(categoryId, { year, month, enabled = true } = {}) {
  return useQuery({
    queryKey: budgetsKeys.categoryHistory(categoryId),
    queryFn: () =>
      categoryBudgetsAPI
        .getCategoryHistory(categoryId, { year, month })
        .then((r) => unwrap(r, [])),
    enabled: enabled && Boolean(categoryId),
  });
}

/** Atualiza ``default_monthly_budget`` — aplica-se a todos os meses. */
export function useUpdateCategoryDefaultBudgetMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, amount }) =>
      expensesAPI
        .updateCategory(categoryId, { default_monthly_budget: amount })
        .then((r) => unwrap(r)),
    onMutate: async ({ categoryId, amount }) => {
      await qc.cancelQueries({ queryKey: budgetsKeys.all, exact: false });
      const snapshot = qc.getQueriesData({ queryKey: budgetsKeys.all });
      qc.setQueriesData({ queryKey: budgetsKeys.all }, (old) => {
        if (!old?.groups) return old;
        return {
          ...old,
          groups: old.groups.map((g) => {
            const updatedCats = (g.categories || []).map((cat) => {
              const id = cat.category_id || cat.id;
              if (String(id) !== String(categoryId)) return cat;
              return { ...cat, budget_eur: amount };
            });
            const newSum = updatedCats.reduce(
              (s, cat) => s + Number(cat.budget_eur || 0),
              0,
            );
            return {
              ...g,
              categories: updatedCats,
              categories_budget_sum_eur: newSum.toFixed(2),
            };
          }),
        };
      });
      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          qc.setQueryData(key, data);
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: budgetsKeys.all, exact: false });
      qc.invalidateQueries({ queryKey: ['expenses', 'categories'], exact: false });
      invalidateGuidedFlow(qc);
    },
  });
}

export function useBudgetAlertsQuery(filters, queryOptions = {}) {
  return useQuery({
    queryKey: budgetsKeys.alerts(filters),
    queryFn: () => budgetAlertsAPI.list(filters).then((r) => unwrap(r, [])),
    ...queryOptions,
  });
}

export function useDismissBudgetAlertMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => budgetAlertsAPI.dismiss(id).then((r) => unwrap(r)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['budgets', 'alerts'], exact: false });
      qc.invalidateQueries({ queryKey: ['budgets', 'overview'], exact: false });
    },
  });
}
